import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import { Permissions, RequirePermissions } from '@/core/permissions/export';
import { useTimer } from '@/shared/hooks/export';
import { DateFormat } from '@/core/date/export';
import { CountryFlag } from '@/shared/components/export';
import EventBadges from '@/modules/calendar/components/event-badges';
import InstantAnswersRowEntry from '@/modules/calendar/components/data-calendar/rows/instant-answers-row-entry';
import AuctionRowEntry from '@/modules/calendar/components/data-calendar/rows/auction-row-entry';
import DataRowEntry from '@/modules/calendar/components/data-calendar/rows/data-row-entry';
import { CoverageBadges } from '@/modules/calendar/components/data-calendar/coverage/coverage-badges';

import calendarStyles from '@/modules/calendar/components/data-calendar/list/calendar-daily-view.module.scss';

interface CalendarDailyViewProps {
  calendar?: NewsEvent[];
  setEvent: Function;
}

function FlagAndRating({ event }: { event: NewsEvent }) {
  return (
    <>
      <CountryFlag country={event.country} width={26} height={13} />
      <div className={'small'}>
        <EventBadges event={event} />
      </div>
    </>
  );
}

export default function CalendarDailyView({ calendar, setEvent }: CalendarDailyViewProps) {
  const [nextDate, setNextDate] = useState<Dayjs | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  const toggleEventExpansion = (eventId: string) => {
    setExpandedEventId(prev => (prev === eventId ? null : eventId));
  };

  const refreshNextDate = () => {
    let found = false;

    calendar?.forEach(value => {
      const currentTime = dayjs(dayjs());

      const today = currentTime.format('YYYY-MM-DD');
      const eventDate = dayjs(value.date);

      if (eventDate.format('YYYY-MM-DD') === today) {
        if (!found) {
          if (eventDate.add(30, 'seconds').isAfter(currentTime)) {
            found = true;
            setNextDate(eventDate);
          }
        }
      }
    });
  };

  useTimer(
    30_000,
    () => {
      refreshNextDate();
    },
    true,
  );

  return (
    <>
      <table className={'table table-sm text-container-sm-start container-responsive-text-sm'}>
        <thead className={calendarStyles.stickyHeader}>
          <tr className={'text-text-dark bg-quaternary'}>
            <th className={'text-text-dark bg-quaternary'}>Time</th>
            <th className={'text-text-dark bg-quaternary'}></th>
            <th className={'text-text-dark bg-quaternary'}>Figure</th>
            <th className={'text-text-dark bg-quaternary'}></th>
            <th className={'text-text-dark bg-quaternary'}>Fcst</th>
            <th className={'fw-bold  bg-quaternary'}>Act</th>
            <th className={'text-text-dark bg-quaternary'}>Diff</th>
            <th className={'text-text-dark bg-quaternary'}>
              Prev<span className={'fw-bold text-body'}> / Rev</span>{' '}
            </th>
          </tr>
        </thead>

        {calendar?.map((event, index) => {
          const secondaryEntries = [];
          const rows: JSX.Element[] = [];

          event.auction &&
            event.auction.auctionProducts.forEach(value => {
              rows.push(<AuctionRowEntry auctionProduct={value} />);
            });

          event.dataSeriesEntries &&
            event.dataSeriesEntries.forEach(value => {
              if (value.isPreviousRevision) return;

              rows.push(
                <DataRowEntry
                  event={event}
                  dataSeriesEntry={value}
                  date={event.date}
                  reportingPeriod={event.reportingPeriod}
                  isNext={nextDate?.isSame(dayjs(event.date)) || false}
                />,
              );
            });

          event.instantAnswers &&
            event.instantAnswers.forEach(value => {
              rows.push(<InstantAnswersRowEntry instantAnswers={value} />);
            });

          const isExpanded = expandedEventId === event.id;
          const rowLimit = event.displayLines ?? 9;
          const maxSecondaryRows = isExpanded ? rows.length : Math.min(rowLimit, rows.length);

          for (let i = 1; i < maxSecondaryRows; i++) {
            secondaryEntries.push(
              <tr key={'row-' + event.id + '-' + i + '-' + index} onClick={() => setEvent(event)}>
                {rows[i]}
              </tr>,
            );
          }

          if (rows.length > rowLimit) {
            const remainingRows = rows.length - maxSecondaryRows;

            if (remainingRows === 1) {
              secondaryEntries.push(
                <tr
                  key={'row-' + event.id + '-' + maxSecondaryRows + '-' + index}
                  onClick={() => setEvent(event)}
                >
                  {rows[maxSecondaryRows]}
                </tr>,
              );
            } else {
              secondaryEntries.push(
                <tr key={'expand-' + event.id + '-' + index}>
                  <td
                    colSpan={8}
                    className="text-center py-2 small"
                    role="button"
                    tabIndex={0}
                    onClick={e => {
                      e.stopPropagation();
                      toggleEventExpansion(event.id);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleEventExpansion(event.id);
                      }
                    }}
                  >
                    <span className="text-tertiary">
                      {isExpanded ? (
                        <>
                          <FontAwesomeIcon icon={faChevronUp} className="me-1" />
                          Show less
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faChevronDown} className="me-1" />
                          Show {remainingRows} more
                        </>
                      )}
                    </span>
                  </td>
                </tr>,
              );
            }
          }

          //fallback in case of no other rows
          if (rows.length === 0) {
            rows.push(
              <>
                <td colSpan={6}>{event.title}</td>
              </>,
            );
          }

          return (
            <tbody key={'cal-row-' + index} className={calendarStyles.dayTable}>
              <tr className={'data-calendar-top-row'} onClick={() => setEvent(event)}>
                <td
                  rowSpan={isExpanded ? rows.length + 1 : rows.length}
                  className={'head-column'}
                  style={{ width: '5rem' }}
                >
                  <div className={'text-muted'}>
                    <div>
                      {' '}
                      <DateFormat format={'h:mm a'} date={event.date} />
                    </div>

                    <span className={'d-container-inline d-container-sm-none text-start'}>
                      <FlagAndRating event={event} />
                    </span>

                    <RequirePermissions deny={Permissions.EXCLUDE_NEWS}>
                      <CoverageBadges event={event} />
                    </RequirePermissions>
                  </div>
                </td>
                <td
                  rowSpan={isExpanded ? rows.length + 1 : rows.length}
                  style={{ maxWidth: '4em' }}
                  className={'text-end'}
                >
                  <span className={'d-container-none d-container-sm-inline text-end'}>
                    <FlagAndRating event={event} />
                  </span>
                </td>
                {rows[0]}
              </tr>
              {secondaryEntries}
            </tbody>
          );
        })}
      </table>

      {calendar && calendar.length === 0 && (
        <div className={'alert alert-success text-center'}>
          <div>No releases for today.</div>
        </div>
      )}
    </>
  );
}
